import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { Group, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import * as yup from "yup";
import { AutoDataTable } from "../../../components/AutoDataTable";
import { CreationForm } from "../../../components/form/CreationForm";
import { ListPageHeader } from "../../../components/PageHeader";
import { MetadataEditor } from "../components/MetadataEditor";
import { useOrganizationLimits } from "../../../../../business/limits.hook";
import { LimitBadge } from "../../../components/LimitIndicator";

const ITEM_CATEGORIES_QUERY = gql`
  query ($cursor: String, $count: Float) {
    itemCategories(page: { first: $count, after: $cursor }) {
      nodes {
        id
        name
        description
        externalId
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_ITEM_CATEGORY_MUTATION = gql`
  mutation ($id: ID!) {
    itemCategoryDelete(id: $id) {
      id
    }
  }
`;

const CREATE_ITEM_CATEGORY_MUTATION = gql`
  mutation ($input: ItemCategoryCreateInput!) {
    itemCategoryCreate(input: $input) {
      id
      name
      description
      externalId
    }
  }
`;

const UPDATE_ITEM_CATEGORY_MUTATION = gql`
  mutation ($id: ID!, $input: ItemCategoryUpdateInput!) {
    itemCategoryUpdate(id: $id, input: $input) {
      id
      name
      description
      externalId
    }
  }
`;

const ITEMS_QUERY = gql`
  query ($cursor: String, $count: Float) {
    items(page: { first: $count, after: $cursor }) {
      nodes {
        id
        name
        description
        categoryId
        externalId
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const CATEGORIES_FOR_DROPDOWN_QUERY = gql`
  query {
    itemCategories(page: { first: 1000 }) {
      nodes {
        id
        name
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_ITEM_MUTATION = gql`
  mutation ($id: ID!) {
    itemDelete(id: $id) {
      id
    }
  }
`;

const CREATE_ITEM_MUTATION = gql`
  mutation ($input: ItemCreateInput!) {
    itemCreate(input: $input) {
      id
      name
      description
      categoryId
      externalId
    }
  }
`;

const UPDATE_ITEM_MUTATION = gql`
  mutation ($id: ID!, $input: ItemUpdateInput!) {
    itemUpdate(id: $id, input: $input) {
      id
      name
      description
      categoryId
      externalId
    }
  }
`;

export function CreateItemCategoryForm({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const [createItemCategory] = useMutation<any>(CREATE_ITEM_CATEGORY_MUTATION, {
    refetchQueries: ["itemCategories"],
  });

  return (
    <CreationForm
      title="Create Item Category"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter category name",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this category is for",
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await createItemCategory({
          variables: {
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Item category created",
          message: "The item category has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditItemCategoryForm({
  categoryId,
  onClose,
}: {
  categoryId: string;
  onClose: () => void;
}) {
  const [updateItemCategory] = useMutation<any>(UPDATE_ITEM_CATEGORY_MUTATION, {
    refetchQueries: ["itemCategories"],
  });
  const { data, loading } = useQuery<any>(
    gql`
      query ($id: ID!) {
        itemCategories(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            description
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: categoryId },
      skip: !categoryId,
    },
  );

  if (loading) return <div>Loading...</div>;

  const category = data?.itemCategories?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Item Category"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter category name",
          defaultValue: category?.name,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID",
          defaultValue: category?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this category is for",
          defaultValue: category?.description,
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: category?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor
              onChange={onChange}
              value={category?.metadata || []}
            />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await updateItemCategory({
          variables: {
            id: categoryId,
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Item category updated",
          message: "The item category has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

export function CreateItemForm({ onSuccess }: { onSuccess?: () => void }) {
  const [createItem] = useMutation<any>(CREATE_ITEM_MUTATION, {
    refetchQueries: ["items"],
  });
  const { data: categoriesData } = useQuery<any>(CATEGORIES_FOR_DROPDOWN_QUERY);

  const categoryOptions =
    categoriesData?.itemCategories?.nodes?.map((category: any) => ({
      value: category.id,
      label: category.name,
    })) || [];

  return (
    <CreationForm
      title="Create Item"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter item name",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this item is",
        },
        categoryId: {
          type: "SELECT",
          label: "Category",
          yupConfig: yup.string(),
          options: [{ label: "No Category", value: "" }, ...categoryOptions],
          placeholder: "Select a category (optional)",
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        const input = {
          name: values.name,
          description: values.description,
          externalId: values.externalId || undefined,
          categoryId: values.categoryId || undefined,
          metadata: values.metadata || [],
        };
        await createItem({
          variables: { input },
        });
        notifications.show({
          title: "Item created",
          message: "The item has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditItemForm({
  itemId,
  onClose,
}: {
  itemId: string;
  onClose: () => void;
}) {
  const [updateItem] = useMutation<any>(UPDATE_ITEM_MUTATION, {
    refetchQueries: ["items"],
  });
  const { data: itemData, loading: itemLoading } = useQuery<any>(
    gql`
      query ($id: ID!) {
        items(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            description
            categoryId
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: itemId },
      skip: !itemId,
    },
  );

  const { data: categoriesData } = useQuery<any>(CATEGORIES_FOR_DROPDOWN_QUERY);

  const categoryOptions =
    categoriesData?.itemCategories?.nodes?.map((category: any) => ({
      value: category.id,
      label: category.name,
    })) || [];

  if (itemLoading) return <div>Loading...</div>;

  const item = itemData?.items?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Item"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter item name",
          defaultValue: item?.name,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
          defaultValue: item?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this item is",
          defaultValue: item?.description,
        },
        categoryId: {
          type: "SELECT",
          label: "Category",
          yupConfig: yup.string(),
          options: [{ label: "No Category", value: "" }, ...categoryOptions],
          placeholder: "Select a category (optional)",
          defaultValue: item?.categoryId || "",
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: item?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={item?.metadata || []} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        const input = {
          name: values.name,
          description: values.description,
          externalId: values.externalId || undefined,
          categoryId: values.categoryId || undefined,
          metadata: values.metadata || [],
        };
        await updateItem({
          variables: { id: itemId, input },
        });
        notifications.show({
          title: "Item updated",
          message: "The item has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

export function ItemsCatalogCategoriesList() {
  const handleEditClick = (item: any) => {
    const modalId = `edit-item-category-${item.id}`;
    modals.open({
      modalId,
      title: "Edit Item Category",
      size: "lg",
      children: (
        <EditItemCategoryForm
          categoryId={item.id}
          onClose={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <AutoDataTable
      itemPath="items-catalog/categories"
      createButtonText={false}
      query={ITEM_CATEGORIES_QUERY}
      deleteMutation={DELETE_ITEM_CATEGORY_MUTATION}
      linkClick={handleEditClick}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
        {
          accessor: "externalId",
          title: "External ID",
          rawElement: (record: any) => (
            <Text size="sm" c="dimmed">
              {record.externalId || "-"}
            </Text>
          ),
        },
        {
          accessor: "description",
          title: "Description",
        },
      ]}
    />
  );
}

export function ItemsCatalogItemsList() {
  const { data: categoriesData } = useQuery<any>(CATEGORIES_FOR_DROPDOWN_QUERY);
  const categoryMap = new Map<string, string>(
    categoriesData?.itemCategories?.nodes?.map((c: any) => [c.id, c.name]) ||
      [],
  );

  const handleEditClick = (item: any) => {
    const modalId = `edit-item-${item.id}`;
    modals.open({
      modalId,
      title: "Edit Item",
      size: "lg",
      children: (
        <EditItemForm
          itemId={item.id}
          onClose={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <AutoDataTable
      itemPath="items-catalog/items"
      createButtonText={false}
      query={ITEMS_QUERY}
      deleteMutation={DELETE_ITEM_MUTATION}
      linkClick={handleEditClick}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
        {
          accessor: "externalId",
          title: "External ID",
          rawElement: (record: any) => (
            <Text size="sm" c="dimmed">
              {record.externalId || "-"}
            </Text>
          ),
        },
        {
          accessor: "categoryId",
          title: "Category",
          rawElement: (record: any) => (
            <Text size="sm">{categoryMap.get(record.categoryId) || "-"}</Text>
          ),
        },
        {
          accessor: "description",
          title: "Description",
        },
      ]}
    />
  );
}

export function ItemsCatalogHome() {
  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Items Catalog"
        subtitle="Manage virtual items and categories for your gamification system"
        showBreadcrumbs={false}
      />
    </div>
  );
}

const openCreateItemCategoryModal = () => {
  const modalId = "create-item-category";
  modals.open({
    modalId,
    title: "Create Item Category",
    size: "lg",
    children: (
      <CreateItemCategoryForm onSuccess={() => modals.close(modalId)} />
    ),
  });
};

export function ItemsCatalogCategories() {
  const { getLimit } = useOrganizationLimits(["ITEM_CATEGORIES"]);
  const categoriesLimit = getLimit("ITEM_CATEGORIES");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Item Categories"
        subtitle="Organize items into logical groups for easier management"
        createLabel="Create Category"
        onCreate={openCreateItemCategoryModal}
        showBreadcrumbs={false}
      />
      {categoriesLimit && (
        <Group mb="md">
          <LimitBadge limit={categoriesLimit} label="Categories limit" />
        </Group>
      )}
      <ItemsCatalogCategoriesList />
    </div>
  );
}

const openCreateItemModal = () => {
  const modalId = "create-item";
  modals.open({
    modalId,
    title: "Create Item",
    size: "lg",
    children: <CreateItemForm onSuccess={() => modals.close(modalId)} />,
  });
};

export function ItemsCatalogItems() {
  const { getLimit } = useOrganizationLimits(["ITEMS"]);
  const itemsLimit = getLimit("ITEMS");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Items"
        subtitle="Virtual goods that players can earn, purchase, or receive as rewards"
        createLabel="Create Item"
        onCreate={openCreateItemModal}
        showBreadcrumbs={false}
      />
      {itemsLimit && (
        <Group mb="md">
          <LimitBadge limit={itemsLimit} label="Items limit" />
        </Group>
      )}
      <ItemsCatalogItemsList />
    </div>
  );
}
